// Implements: F-UIX-018, F-RPT-009
/**
 * The exports of one organization's or team's consumption screen (UIX.5, RPT.8). Mounted inside
 * the screen's container, which is keyed by its target (checklist 14), so a format, a download
 * under way or a refusal never follows the person to another scope.
 *
 * A download is a plain `GET` through the shared client (a refusal raises its `ApiError`, never a
 * file holding the error body); its bytes are saved under the server's file name, or the report's
 * own name when the answer names none. A refusal (an export over the row cap, a `404`) is shown
 * with the server's sentence and nothing is saved. No browser storage.
 */

import { useState } from "react";
import type { ReactElement } from "react";

import { asApiError } from "../../../shared/api/asApiError";
import { useTranslation } from "../../../shared/i18n";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import { saveBlob } from "../../../shared/ui/saveBlob";
import type { ConsumptionGrouping, ExportFormat, ExportReport, ReportTarget } from "../../../types/reports";
import { downloadScopeExport } from "../api/scopeExportsApi";
import { ScopeExports } from "../components/ScopeExports";
import { defaultExportName, exportsOf } from "../model/scopeExports";

export interface ScopeExportsContainerProps {
  readonly target: ReportTarget;
  /** The grouping on screen, which the consumption export follows. */
  readonly grouping: ConsumptionGrouping;
}

export function ScopeExportsContainer({ target, grouping }: ScopeExportsContainerProps): ReactElement {
  const { t } = useTranslation();
  const [format, setFormat] = useState<ExportFormat>("csv");
  const [downloading, setDownloading] = useState<ExportReport | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const onDownload = async (report: ExportReport): Promise<void> => {
    setDownloading(report);
    setFailure(null);
    try {
      const file = await downloadScopeExport(target, report, format, grouping);
      saveBlob(file.blob, file.filename ?? defaultExportName(target, report, format));
    } catch (cause) {
      setFailure(userMessage(asApiError(cause), t("backoffice.exports.failed")));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <ScopeExports
      reports={exportsOf(target)}
      format={format}
      downloading={downloading}
      failure={failure}
      onFormat={setFormat}
      onDownload={(report) => {
        void onDownload(report);
      }}
    />
  );
}
